export const STRATEGO_COLORS = ["red", "blue"] as const;
export type StrategoColor = typeof STRATEGO_COLORS[number];

export const STRATEGO_RANKS = ["flag", "spy", "scout", "miner", "sergeant", "lieutenant", "captain", "major", "colonel", "general", "marshal", "bomb"] as const;
export type StrategoRank = typeof STRATEGO_RANKS[number];

export const STRATEGO_PIECE_COUNTS: Record<StrategoRank, number> = {
  flag: 1, spy: 1, scout: 8, miner: 5, sergeant: 4, lieutenant: 4,
  captain: 4, major: 3, colonel: 2, general: 1, marshal: 1, bomb: 6,
};

export const STRATEGO_RANK_STRENGTH: Record<StrategoRank, number> = {
  flag: 0, spy: 1, scout: 2, miner: 3, sergeant: 4, lieutenant: 5,
  captain: 6, major: 7, colonel: 8, general: 9, marshal: 10, bomb: 11,
};

export const STRATEGO_LAKES = new Set([42, 43, 46, 47, 52, 53, 56, 57]);

export type StrategoPiece = {
  id: string;
  color: StrategoColor;
  rank: StrategoRank;
  index: number;
};

export type StrategoBattle = {
  attackerColor: StrategoColor;
  attackerRank: StrategoRank;
  defenderColor: StrategoColor;
  defenderRank: StrategoRank;
  from: number;
  to: number;
  outcome: "attacker" | "defender" | "both" | "flag";
};

export type StrategoLastMove = {
  color: StrategoColor;
  pieceId: string;
  rank: StrategoRank;
  from: number;
  to: number;
  distance: number;
  attacked: boolean;
};

type StrategoOscillation = { pieceId: string; from: number; to: number; traversals: number } | null;

export type StrategoState = {
  status: "lobby" | "setup" | "playing" | "finished";
  playerOrder: string[];
  playerColors: Record<string, StrategoColor>;
  colorPlayers: Record<StrategoColor, string>;
  pieces: Record<string, StrategoPiece>;
  board: Array<string | null>;
  ready: Record<StrategoColor, boolean>;
  turnColor: StrategoColor;
  winnerId: string | null;
  winnerColor: StrategoColor | null;
  winReason: "flag" | "immobilized" | null;
  lastMove: StrategoLastMove | null;
  lastBattle: StrategoBattle | null;
  moveCount: number;
  captured: Record<StrategoColor, StrategoRank[]>;
  oscillation: Record<StrategoColor, StrategoOscillation>;
};

export type StrategoAction =
  | { type: "setup_shuffle" }
  | { type: "setup_swap"; first: unknown; second: unknown }
  | { type: "setup_ready" }
  | { type: "move"; from: unknown; to: unknown };

const opponent = (color: StrategoColor): StrategoColor => color === "red" ? "blue" : "red";
const rowOf = (index: number) => Math.floor(index / 10);
const colOf = (index: number) => index % 10;
const validIndex = (value: unknown): value is number => Number.isInteger(value) && Number(value) >= 0 && Number(value) < 100;
const isMobileRank = (rank: StrategoRank) => rank !== "flag" && rank !== "bomb";
const setupIndices = (color: StrategoColor) => {
  const firstRow = color === "blue" ? 0 : 6;
  return Array.from({ length: 40 }, (_, offset) => (firstRow * 10) + offset);
};

function shuffled<T>(items: T[], random: () => number) {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.max(0, Math.min(.999999999, random())) * (index + 1));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
}

function armyRanks() {
  return STRATEGO_RANKS.flatMap((rank) => Array.from({ length: STRATEGO_PIECE_COUNTS[rank] }, () => rank));
}

export function createInitialStrategoState(): StrategoState {
  return {
    status: "lobby",
    playerOrder: [],
    playerColors: {},
    colorPlayers: { red: "", blue: "" },
    pieces: {},
    board: Array(100).fill(null),
    ready: { red: false, blue: false },
    turnColor: "red",
    winnerId: null,
    winnerColor: null,
    winReason: null,
    lastMove: null,
    lastBattle: null,
    moveCount: 0,
    captured: { red: [], blue: [] },
    oscillation: { red: null, blue: null },
  };
}

export function hydrateStrategoState(value: Partial<StrategoState> | null | undefined): StrategoState {
  const initial = createInitialStrategoState();
  if (!value) return initial;
  return {
    ...initial,
    ...value,
    playerOrder: Array.isArray(value.playerOrder) ? value.playerOrder : [],
    playerColors: { ...(value.playerColors || {}) },
    colorPlayers: { ...initial.colorPlayers, ...(value.colorPlayers || {}) },
    pieces: { ...(value.pieces || {}) },
    board: Array.isArray(value.board) && value.board.length === 100 ? value.board : initial.board,
    ready: { ...initial.ready, ...(value.ready || {}) },
    captured: { red: [...(value.captured?.red || [])], blue: [...(value.captured?.blue || [])] },
    oscillation: { ...initial.oscillation, ...(value.oscillation || {}) },
  };
}

function deployColor(state: StrategoState, color: StrategoColor, random: () => number) {
  const oldPieces = Object.values(state.pieces).filter((piece) => piece.color === color);
  for (const piece of oldPieces) state.board[piece.index] = null;
  const ranks = shuffled(armyRanks(), random);
  const positions = setupIndices(color);
  ranks.forEach((rank, number) => {
    const id = `${color}-${number}`;
    const index = positions[number];
    state.pieces[id] = { id, color, rank, index };
    state.board[index] = id;
  });
}

export function startStratego(state: StrategoState, playerIds: string[], random: () => number = Math.random) {
  if (state.status !== "lobby" || playerIds.length !== 2 || new Set(playerIds).size !== 2 || playerIds.some((id) => !id)) return false;
  const fresh = createInitialStrategoState();
  Object.assign(state, fresh);
  state.status = "setup";
  state.playerOrder = [...playerIds];
  state.playerColors = { [playerIds[0]]: "red", [playerIds[1]]: "blue" };
  state.colorPlayers = { red: playerIds[0], blue: playerIds[1] };
  deployColor(state, "red", random);
  deployColor(state, "blue", random);
  return true;
}

function pathIsClear(state: StrategoState, from: number, to: number) {
  const rowStep = rowOf(from) === rowOf(to) ? 0 : Math.sign(rowOf(to) - rowOf(from));
  const colStep = colOf(from) === colOf(to) ? 0 : Math.sign(colOf(to) - colOf(from));
  let row = rowOf(from) + rowStep;
  let col = colOf(from) + colStep;
  while ((row * 10) + col !== to) {
    const index = (row * 10) + col;
    if (STRATEGO_LAKES.has(index) || state.board[index]) return false;
    row += rowStep;
    col += colStep;
  }
  return true;
}

function isOscillationForbidden(state: StrategoState, piece: StrategoPiece, from: number, to: number) {
  const previous = state.oscillation[piece.color];
  return Boolean(previous && previous.pieceId === piece.id && previous.from === to && previous.to === from && previous.traversals >= 3);
}

export function strategoSetupHasOpeningMove(state: StrategoState, color: StrategoColor) {
  const frontRow = color === "red" ? 6 : 3;
  const forward = color === "red" ? -10 : 10;
  return Object.values(state.pieces).some((piece) => piece.color === color
    && rowOf(piece.index) === frontRow
    && isMobileRank(piece.rank)
    && !STRATEGO_LAKES.has(piece.index + forward));
}

function isLegalMove(state: StrategoState, color: StrategoColor, from: number, to: number) {
  if (state.status !== "playing" || state.turnColor !== color || from === to || STRATEGO_LAKES.has(to)) return false;
  const pieceId = state.board[from];
  const piece = pieceId ? state.pieces[pieceId] : null;
  if (!piece || piece.color !== color || !isMobileRank(piece.rank) || isOscillationForbidden(state, piece, from, to)) return false;
  const targetId = state.board[to];
  if (targetId && state.pieces[targetId]?.color === color) return false;
  const sameRow = rowOf(from) === rowOf(to);
  const sameColumn = colOf(from) === colOf(to);
  if (!sameRow && !sameColumn) return false;
  const distance = Math.abs(rowOf(from) - rowOf(to)) + Math.abs(colOf(from) - colOf(to));
  if (piece.rank !== "scout" && distance !== 1) return false;
  return piece.rank !== "scout" || pathIsClear(state, from, to);
}

export function strategoLegalMoves(state: StrategoState, playerId: string) {
  const color = state.playerColors[playerId];
  if (!color || state.status !== "playing" || state.turnColor !== color) return [] as Array<{ from: number; to: number }>;
  const moves: Array<{ from: number; to: number }> = [];
  for (const piece of Object.values(state.pieces)) {
    if (piece.color !== color || !isMobileRank(piece.rank)) continue;
    if (piece.rank === "scout") {
      for (const [rowStep, colStep] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
        let row = rowOf(piece.index) + rowStep;
        let col = colOf(piece.index) + colStep;
        while (row >= 0 && row < 10 && col >= 0 && col < 10) {
          const to = (row * 10) + col;
          if (STRATEGO_LAKES.has(to)) break;
          const targetId = state.board[to];
          if (targetId && state.pieces[targetId]?.color === color) break;
          if (isLegalMove(state, color, piece.index, to)) moves.push({ from: piece.index, to });
          if (targetId) break;
          row += rowStep;
          col += colStep;
        }
      }
    } else {
      for (const to of [piece.index - 10, piece.index + 10, piece.index - 1, piece.index + 1]) {
        if (!validIndex(to) || (Math.abs(colOf(piece.index) - colOf(to)) + Math.abs(rowOf(piece.index) - rowOf(to)) !== 1)) continue;
        if (isLegalMove(state, color, piece.index, to)) moves.push({ from: piece.index, to });
      }
    }
  }
  return moves;
}

function attackerWins(attacker: StrategoRank, defender: StrategoRank) {
  if (defender === "bomb") return attacker === "miner";
  if (attacker === "spy" && defender === "marshal") return true;
  return STRATEGO_RANK_STRENGTH[attacker] > STRATEGO_RANK_STRENGTH[defender];
}

function finish(state: StrategoState, winnerColor: StrategoColor, reason: "flag" | "immobilized") {
  state.status = "finished";
  state.winnerColor = winnerColor;
  state.winnerId = state.colorPlayers[winnerColor];
  state.winReason = reason;
}

function applyMove(state: StrategoState, color: StrategoColor, from: number, to: number) {
  if (!isLegalMove(state, color, from, to)) return false;
  const attackerId = state.board[from]!;
  const attacker = state.pieces[attackerId];
  const defenderId = state.board[to];
  const distance = Math.abs(rowOf(from) - rowOf(to)) + Math.abs(colOf(from) - colOf(to));
  const previous = state.oscillation[color];
  state.oscillation[color] = previous && previous.pieceId === attackerId && previous.from === to && previous.to === from
    ? { pieceId: attackerId, from, to, traversals: previous.traversals + 1 }
    : { pieceId: attackerId, from, to, traversals: 1 };
  state.lastBattle = null;
  state.lastMove = { color, pieceId: attackerId, rank: attacker.rank, from, to, distance, attacked: Boolean(defenderId) };
  state.board[from] = null;
  if (!defenderId) {
    state.board[to] = attackerId;
    attacker.index = to;
  } else {
    const defender = state.pieces[defenderId];
    let outcome: StrategoBattle["outcome"];
    if (defender.rank === "flag") outcome = "flag";
    else if (attacker.rank !== "miner" && defender.rank === "bomb") outcome = "defender";
    else if (STRATEGO_RANK_STRENGTH[attacker.rank] === STRATEGO_RANK_STRENGTH[defender.rank]) outcome = "both";
    else outcome = attackerWins(attacker.rank, defender.rank) ? "attacker" : "defender";
    state.lastBattle = { attackerColor: color, attackerRank: attacker.rank, defenderColor: defender.color, defenderRank: defender.rank, from, to, outcome };
    if (outcome === "attacker" || outcome === "flag") {
      state.captured[color].push(defender.rank);
      delete state.pieces[defenderId];
      state.board[to] = attackerId;
      attacker.index = to;
    } else if (outcome === "defender") {
      state.captured[defender.color].push(attacker.rank);
      delete state.pieces[attackerId];
    } else {
      state.captured[color].push(defender.rank);
      state.captured[defender.color].push(attacker.rank);
      delete state.pieces[attackerId];
      delete state.pieces[defenderId];
      state.board[to] = null;
    }
    if (outcome === "flag") finish(state, color, "flag");
  }
  state.moveCount += 1;
  if (state.status === "playing") {
    state.turnColor = opponent(color);
    if (!strategoLegalMoves(state, state.colorPlayers[state.turnColor]).length) finish(state, color, "immobilized");
  }
  return true;
}

export function applyStrategoAction(state: StrategoState, playerId: string, action: StrategoAction, random: () => number = Math.random) {
  const color = state.playerColors[playerId];
  if (!color || !action || typeof action !== "object") return false;
  if (action.type === "setup_shuffle") {
    if (state.status !== "setup" || state.ready[color]) return false;
    deployColor(state, color, random);
    return true;
  }
  if (action.type === "setup_swap") {
    if (state.status !== "setup" || state.ready[color] || !validIndex(action.first) || !validIndex(action.second) || action.first === action.second) return false;
    const firstId = state.board[action.first];
    const secondId = state.board[action.second];
    if (!firstId || !secondId || state.pieces[firstId]?.color !== color || state.pieces[secondId]?.color !== color) return false;
    state.board[action.first] = secondId;
    state.board[action.second] = firstId;
    state.pieces[firstId].index = action.second;
    state.pieces[secondId].index = action.first;
    return true;
  }
  if (action.type === "setup_ready") {
    if (state.status !== "setup" || state.ready[color]) return false;
    state.ready[color] = true;
    if (state.ready.red && state.ready.blue) {
      state.status = "playing";
      state.turnColor = "red";
      if (!strategoLegalMoves(state, state.colorPlayers.red).length) finish(state, "blue", "immobilized");
    }
    return true;
  }
  if (action.type === "move" && validIndex(action.from) && validIndex(action.to)) return applyMove(state, color, action.from, action.to);
  return false;
}

export function strategoPublicView(state: StrategoState, viewerId?: string) {
  const viewerColor = viewerId ? state.playerColors[viewerId] || null : null;
  const revealAll = state.status === "finished";
  const pieces = Object.values(state.pieces).map((piece) => ({
    id: piece.id,
    color: piece.color,
    index: piece.index,
    rank: revealAll || piece.color === viewerColor ? piece.rank : null,
    movable: revealAll || piece.color === viewerColor ? isMobileRank(piece.rank) : null,
  }));
  const legalMoves = viewerId ? strategoLegalMoves(state, viewerId) : [];
  return {
    status: state.status,
    playerOrder: [...state.playerOrder],
    playerColors: { ...state.playerColors },
    colorPlayers: { ...state.colorPlayers },
    pieces,
    ready: { ...state.ready },
    turnColor: state.turnColor,
    winnerId: state.winnerId,
    winnerColor: state.winnerColor,
    winReason: state.winReason,
    lastMove: state.lastMove ? {
      color: state.lastMove.color,
      pieceId: state.lastMove.pieceId,
      from: state.lastMove.from,
      to: state.lastMove.to,
      distance: state.lastMove.distance,
      attacked: state.lastMove.attacked,
      rank: revealAll || viewerColor === state.lastMove.color || state.lastMove.distance > 1 ? state.lastMove.rank : null,
    } : null,
    lastBattle: state.lastBattle ? { ...state.lastBattle } : null,
    moveCount: state.moveCount,
    captured: { red: [...state.captured.red], blue: [...state.captured.blue] },
    viewerColor,
    setupHasOpeningMove: viewerColor && state.status === "setup" ? strategoSetupHasOpeningMove(state, viewerColor) : null,
    canAct: Boolean(viewerColor && ((state.status === "setup" && !state.ready[viewerColor]) || (state.status === "playing" && state.turnColor === viewerColor))),
    legalMoves,
  };
}

export function resetStratego(state: StrategoState) {
  Object.assign(state, createInitialStrategoState());
}
